import { Avatar, Button, Flex, HStack, Stack, Text } from '@chakra-ui/react'
import { ReactNode, useCallback, useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { ISelectOption } from '../../../components/Select'
import { StatusBadge } from '../../../components/StatusBadge'
import { TabErrorLabel } from '../../../components/TabErrorLabel'
import { useAppToast, useHasRole } from '../../../helpers'
import { countErrorsPerTab, firstTabWithErrors, withoutFieldErrors } from '../../../helpers/tabErrorUtils'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { IEmployeeDetailRaw } from '../../../services/Employee/types'
import { JobPosition } from '../../../services/JobPosition/types'
import { TFormErrors } from '../Admission/admissionForm'
import { EmployeeActionModal, TEmployeeAction } from './EmployeeActionModal'
import { EMPLOYEE_STATUS, EMPLOYEE_TAB_DEFS } from './employeeConstants'
import EmployeeDocumentsTab from './EmployeeDocumentsTab'
import { EMPTY_EMPLOYEE_FORM, employeeFormData, employeeFormFromDetail, IEmployeeForm } from './employeeForm'
import { employeeFormPanels, useUsernameSuggestions } from './EmployeeFormPanels'
import { BenefitsSheet, ContactsSheet, HistorySheet, PersonalSheet, ProfessionalSheet } from './EmployeeSheetTabs'

interface IEmployeeViewModalProps {
  isOpen: boolean
  onClose: () => void
  /** The collaborator to show; null while none is chosen, so the modal can stay mounted. */
  employeeId: number | null
  roles: JobPosition[]
  sectors: ISelectOption<number>[]
  /** Called after anything changes, so the list or the kanban reads itself again. */
  onChanged: () => void
}

const service = new EmployeeService()

/**
 * The collaborator's sheet, opened from the admission kanban and from "Colaboradores": every
 * piece of data, the documents and the history, with editing and the actions of each status
 * (approve or reject a pending one, inactivate an active one, reactivate an inactive one).
 */
export function EmployeeViewModal({ isOpen, onClose, employeeId, roles, sectors, onChanged }: IEmployeeViewModalProps) {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const can = {
    edit: hasRole('edit_employees'),
    approve: hasRole('approve_employees'),
    inactivate: hasRole('inactivate_employees'),
    reactivate: hasRole('reactivate_employees')
  }

  const [detail, setDetail] = useState<IEmployeeDetailRaw | null>(null)
  const [loading, setLoading] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<IEmployeeForm>(EMPTY_EMPLOYEE_FORM)
  const [photo, setPhoto] = useState<File | null>(null)
  const [errors, setErrors] = useState<TFormErrors>({})
  const [saving, setSaving] = useState(false)
  const [action, setAction] = useState<TEmployeeAction | null>(null)
  const [isRejectOpen, setIsRejectOpen] = useState(false)
  const usernames = useUsernameSuggestions(form.name, editing, detail?.id)

  const load = useCallback(
    (id: number) => {
      setLoading(true)
      return service
        .show(id)
        .then(setDetail)
        .catch((error) => {
          toast.error('Erro ao carregar o colaborador', error)
          onClose()
        })
        .finally(() => setLoading(false))
    },
    [toast, onClose]
  )

  useEffect(() => {
    if (!isOpen || employeeId === null) return

    setDetail(null)
    setTabIndex(0)
    setEditing(false)
    load(employeeId)
  }, [isOpen, employeeId])

  const startEditing = () => {
    if (!detail) return
    setForm(employeeFormFromDetail(detail))
    setPhoto(null)
    setErrors({})
    setEditing(true)
  }

  /** Drops the errors of the fields (and their items) once they are edited. */
  const update = (patch: Partial<IEmployeeForm>) => {
    setForm((current) => ({ ...current, ...patch }))
    setErrors((current) => withoutFieldErrors(current, Object.keys(patch)))
  }

  const changed = (updated: IEmployeeDetailRaw) => {
    setDetail(updated)
    onChanged()
  }

  const save = () => {
    if (!detail) return

    setSaving(true)
    service
      .update(detail.id, employeeFormData(form, photo, detail.status))
      .then((updated) => {
        toast.success('Colaborador salvo')
        setEditing(false)
        changed(updated)
      })
      .catch((error) => {
        const fieldErrors = error?.response?.data?.errors
        if (fieldErrors) {
          setErrors(fieldErrors)
          const tab = firstTabWithErrors(EMPLOYEE_TAB_DEFS, fieldErrors)
          if (tab >= 0) setTabIndex(tab)
          toast.invalidForm()
          return
        }
        toast.error('Erro ao salvar o colaborador', error)
      })
      .finally(() => setSaving(false))
  }

  const reject = () => {
    if (!detail) return Promise.resolve()

    return service
      .reject(detail.id)
      .then(() => {
        toast.success(`Admissão de ${detail.name ?? 'colaborador'} reprovada`)
        setIsRejectOpen(false)
        onChanged()
        onClose()
      })
      .catch((error) => toast.error('Erro ao reprovar', error))
  }

  const errorCounts = countErrorsPerTab(EMPLOYEE_TAB_DEFS, errors)
  // Errors only exist while editing; the read-only sheet shows the plain labels.
  const tabLabel = (index: number) => (
    <TabErrorLabel label={EMPLOYEE_TAB_DEFS[index].label} count={editing ? errorCounts[index] : 0} />
  )

  const panel = (content: ReactNode) => (loading || !detail ? <LoadingBlock entity="colaborador" /> : content)

  const editPanels = detail
    ? employeeFormPanels({
        form,
        update,
        errors,
        isDisabled: saving,
        status: detail.status,
        photo,
        photoUrl: detail.photo_url,
        onPhotoChange: (file) => {
          setPhoto(file)
          setErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== 'photo')))
        },
        roles,
        sectors,
        usernames,
        currentRoleId: detail.job_position?.id
      })
    : null

  const readPanels = detail && {
    personal: <PersonalSheet detail={detail} />,
    professional: <ProfessionalSheet detail={detail} />,
    contacts: <ContactsSheet detail={detail} />,
    benefits: <BenefitsSheet detail={detail} />
  }

  const panels = editing ? editPanels : readPanels

  const statusActions = detail && !editing && (
    <>
      {detail.status === 'pending' && can.approve && (
        <>
          <ActionButton action="reject" label="Reprovar" showLabel mr={0} onClick={() => setIsRejectOpen(true)} />
          <ActionButton action="approve" label="Aprovar" showLabel mr={0} onClick={() => setAction('approve')} />
        </>
      )}
      {detail.status === 'active' && can.inactivate && (
        <ActionButton action="deactivate" label="Inativar" showLabel mr={0} onClick={() => setAction('inactivate')} />
      )}
      {detail.status === 'inactive' && can.reactivate && (
        <ActionButton action="activate" label="Reativar" showLabel mr={0} onClick={() => setAction('reactivate')} />
      )}
    </>
  )

  const footer = editing ? (
    <HStack spacing={2}>
      <Button variant="ghost" onClick={() => setEditing(false)} isDisabled={saving}>
        Cancelar
      </Button>
      <Button type="submit" colorScheme="magenta" isLoading={saving}>
        Salvar
      </Button>
    </HStack>
  ) : (
    <Flex w="100%" gap={2} wrap="wrap" justify="flex-end">
      {detail && can.edit && <ActionButton action="edit" label="Editar" showLabel mr="auto" onClick={startEditing} />}
      <Button variant="ghost" onClick={onClose}>
        Fechar
      </Button>
      {statusActions}
    </Flex>
  )

  const title = detail ? (
    <Flex align="center" gap={3}>
      <Avatar size="md" src={detail.photo_url ?? undefined} name={detail.name ?? undefined} />
      <Stack spacing={0}>
        <Flex align="center" gap={2} wrap="wrap">
          <Text as="span">{detail.name ?? 'Sem nome'}</Text>
          <StatusBadge status={detail.status} statuses={EMPLOYEE_STATUS} />
        </Flex>
        <Text as="span" variant="caption" fontWeight="normal">
          {[detail.position, detail.sector].filter(Boolean).join(' · ')}
        </Text>
      </Stack>
    </Flex>
  ) : (
    'Colaborador'
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        size="editor"
        onSubmit={editing ? save : undefined}
        noValidate
        footer={footer}
        tabIndex={tabIndex}
        onTabChange={setTabIndex}
        tabs={[
          { label: tabLabel(0), content: panel(panels?.personal) },
          { label: tabLabel(1), content: panel(panels?.professional) },
          { label: tabLabel(2), content: panel(panels?.contacts) },
          { label: tabLabel(3), content: panel(panels?.benefits) },
          {
            label: 'Documentos',
            content: panel(
              detail && (
                <EmployeeDocumentsTab
                  detail={detail}
                  canEdit={can.edit}
                  onChanged={() => detail && load(detail.id).then(onChanged)}
                />
              )
            )
          },
          { label: 'Histórico', content: panel(detail && <HistorySheet detail={detail} />) }
        ]}
      />

      <EmployeeActionModal action={action} detail={detail} onClose={() => setAction(null)} onDone={changed} />

      {detail && (
        <ConfirmDialog
          isOpen={isRejectOpen}
          onClose={() => setIsRejectOpen(false)}
          onConfirm={reject}
          action="reject"
          title={`Reprovar ${detail.name ?? 'colaborador'}`}
          description={
            <>
              A admissão de <strong>{detail.name}</strong> é excluída de vez, com a foto e os documentos, e o card sai
              do kanban.
            </>
          }
          irreversible
        />
      )}
    </>
  )
}
